import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Alert, Select, Tooltip } from '@mantine/core';
import { useMemo, useState } from 'react';
import { getChatDocument } from '../../../api/agents-client/agent-client';
import { isSafeUrl } from '../../../helpers/general-helpers';
import { Loader } from '../../../ui-library/loader/loader';
import { isTextPreviewDocType } from '../../flora/components/flora-document/flora-document.helpers';
import type { Document } from '../../flora/flora.type';
import { TextDocumentViewer } from './agent-document/text-document-viewer';
import { PDF_EMBED_PARAMS, useDocumentPreviewFrame } from './use-document-preview-frame';

interface DocPreviewRailPanelProps {
  /** Previewable (Word, Excel, and plain-text) documents from the conversation, newest first. */
  documents: Document[];
  /**
   * The document to focus, owned by the parent. Set when a specific document
   * card is clicked; when absent (or no longer in the list), the panel falls
   * back to the newest doc — e.g. when opened from the header toolbar toggle.
   */
  selectedDocumentId?: string;
  /** Changes the focused document, e.g. from the in-panel document switcher. */
  onSelectDocument?: (documentId: string) => void;
}

/**
 * Rail panel that previews a Flora document: Word or Excel as a PDF, and CSV,
 * TSV, Markdown, JSON, or plain text rendered from its text.
 *
 * For Word/Excel the backend converts the stored file to PDF (LibreOffice) and
 * serves a short-lived presigned url via `useDocumentPreviewFrame`, which polls
 * while the conversion runs and guards the frame load; the PDF fills the rail
 * in a native browser `<iframe>` once ready. Text types skip the conversion
 * entirely and render in {@link TextDocumentViewer}. Selection is fully
 * controlled by the parent via `selectedDocumentId`/`onSelectDocument`, so a
 * card click and the in-panel `<Select>` share one source of truth.
 *
 * @param {DocPreviewRailPanelProps} props - The previewable documents, the focused id, and the select handler
 * @returns {JSX.Element} The document-preview rail panel
 */
export function DocPreviewRailPanel({ documents, selectedDocumentId, onSelectDocument }: DocPreviewRailPanelProps) {
  // Selection is parent-owned: resolve to the focused doc, falling back to the
  // newest previewable one when nothing is focused or the focused doc has
  // scrolled out of the list.
  const selectedDoc = documents.find((doc) => doc.document_id === selectedDocumentId) ?? documents[0];

  const isTextDocument = isTextPreviewDocType(selectedDoc?.doc_type);
  // Text documents never go through the PDF conversion, so the frame hook is
  // left idle (no preview request) while one is selected.
  const { safePreviewUrl, failed, overlayVisible, onFrameLoad, retry } = useDocumentPreviewFrame(
    isTextDocument ? undefined : selectedDoc?.document_id,
    selectedDoc?.revision,
  );
  const [isDownloading, setIsDownloading] = useState(false);

  // Fetch a fresh presigned url at click time: the one carried on the chat
  // message may have expired since the document was produced.
  const handleDownload = async () => {
    if (!selectedDoc?.document_id || isDownloading) return;
    setIsDownloading(true);
    try {
      const fresh = await getChatDocument(selectedDoc.document_id);
      const url = fresh?.download_url ?? selectedDoc.download_url;
      if (url && isSafeUrl(url)) window.open(url, '_blank', 'noopener,noreferrer');
    } finally {
      setIsDownloading(false);
    }
  };

  const selectData = useMemo(
    () => documents.map((doc) => ({ value: doc.document_id, label: doc.filename })),
    [documents],
  );

  return (
    <PanelRoot>
      <PanelHeader>
        {selectData.length > 1 ? (
          <DocumentSelect
            data={selectData}
            value={selectedDoc?.document_id ?? null}
            onChange={(value) => value && onSelectDocument?.(value)}
            allowDeselect={false}
            size="xs"
            aria-label="Select a document to preview"
          />
        ) : (
          <FileName title={selectedDoc?.filename}>{selectedDoc?.filename}</FileName>
        )}
        {selectedDoc?.document_id && (
          <Tooltip label="Download" position="bottom" withArrow>
            <HeaderButton
              type="button"
              onClick={() => void handleDownload()}
              disabled={isDownloading}
              aria-label={`Download ${selectedDoc.filename}`}
            >
              <Icon icon="material-symbols:download" width={16} height={16} />
            </HeaderButton>
          </Tooltip>
        )}
        {!isTextDocument && safePreviewUrl && (
          <Tooltip label="Open in new tab" position="bottom" withArrow>
            <PopOutLink
              href={safePreviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open preview in new tab"
            >
              <Icon icon="material-symbols:open-in-new" width={16} height={16} />
            </PopOutLink>
          </Tooltip>
        )}
      </PanelHeader>
      <EmbedArea>
        {isTextDocument && selectedDoc && (
          <TextDocumentViewer
            key={selectedDoc.document_id}
            documentId={selectedDoc.document_id}
            revision={selectedDoc.revision}
            docType={selectedDoc.doc_type}
          />
        )}
        {!isTextDocument && overlayVisible && (
          <LoadingWrapper>
            <Loader />
          </LoadingWrapper>
        )}
        {!isTextDocument && failed && (
          <ErrorWrapper>
            <Alert color="red" title="Preview unavailable">
              This document could not be previewed. You can still download it from the chat.
              <RetryButton type="button" onClick={retry}>
                Try again
              </RetryButton>
            </Alert>
          </ErrorWrapper>
        )}
        {!isTextDocument && safePreviewUrl && (
          // First-party, presigned PDF rendered by the browser's native viewer.
          // The content is our own generated output (trusted), so no sandbox is
          // applied. Do not point this frame at untrusted content without a
          // security review. `PDF_EMBED_PARAMS` hides the native toolbar
          // (download/print/Drive); `key` stays the bare url so the fragment
          // never remounts the frame.
          <PreviewFrame
            key={safePreviewUrl}
            src={`${safePreviewUrl}${PDF_EMBED_PARAMS}`}
            title={selectedDoc ? `Preview of ${selectedDoc.filename}` : 'Document preview'}
            referrerPolicy="no-referrer"
            data-testid="doc-preview-iframe"
            onLoad={onFrameLoad}
          />
        )}
      </EmbedArea>
    </PanelRoot>
  );
}

// Linaria-styled Mantine Select so the flex layout applies to Mantine's root
// element via className, instead of an inline `styles` prop (repo convention).
const DocumentSelect = styled(Select)`
  flex: 1;
  min-width: 0;
`;

const PanelRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: var(--base-white);
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--sky-light);
  flex-shrink: 0;
`;

const FileName = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-dark);
`;

const EmbedArea = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
  background: var(--base-white);
`;

const LoadingWrapper = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--base-white);
`;

const PreviewFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
`;

const ErrorWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 96px;
`;

const RetryButton = styled.button`
  display: block;
  margin-top: 8px;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
  color: var(--ink-dark);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: var(--sky-base);
    background: var(--sky-lighter);
  }
`;

const HeaderButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
  color: var(--ink-light);
  flex-shrink: 0;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  &:hover:not(:disabled) {
    border-color: var(--sky-base);
    color: var(--ink-darkest);
    background: var(--sky-lighter);
  }

  &:disabled {
    cursor: progress;
    opacity: 0.6;
  }
`;

const PopOutLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
  color: var(--ink-light);
  flex-shrink: 0;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  &:hover {
    border-color: var(--sky-base);
    color: var(--ink-darkest);
    background: var(--sky-lighter);
  }
`;
